<template>
  <div class="boxes">
    <v-card :loading="loading">
      <v-card-text>
        <div class="header">
          <div class="label">
            Daily Sessions Trend
          </div>
        </div>

        <apexchart
          type="area"
          height="300"
          :options="daily_sessions_trend?.chartOptions"
          :series="daily_sessions_trend?.series"
        />
      </v-card-text>
    </v-card>
    <v-card :loading="loading">
      <v-card-text>
        <div class="header">
          <div class="label">
            Payment Method Distribution
          </div>
        </div>

        <apexchart
          type="pie"
          height="300"
          :options="paymentMethodChartOpt"
          :series="paymentMethodSeries"
        />
      </v-card-text>
    </v-card>
    <v-card :loading="loading">
      <v-card-text>
        <div class="header">
          <div class="label">
            User Segment Distribution
          </div>
        </div>

        <apexchart
          type="pie"
          height="300"
          :options="userSegmentChartOpt"
          :series="userSegmentSeries"
        />
      </v-card-text>
    </v-card>
    <v-card :loading="loading">
      <v-card-text>
        <div class="header">
          <div class="label">
            Platform Distribution
          </div>
        </div>

        <apexchart
          type="donut"
          height="300"
          :options="platformChartOpt"
          :series="platformSeries"
        />
      </v-card-text>
    </v-card>
  </div>
</template>
<script>
import VueApexCharts from "vue-apexcharts";
import { mapGetters } from "vuex";

export default {
  components: {
    apexchart: VueApexCharts,
  },

  props: {
    filtersOptions: {
      type: Object,
      default: () => ({}),
    },
  },

  data() {
    return {
      options: { ...this.filtersOptions },
      // Static chart series/options have been replaced with computed
      // properties below, driven by usersAnalyticsDashboard/getAnalyticsData.
    };
  },

  computed: {
    ...mapGetters({
      authUser: "getUser",
      loading: "usersAnalyticsDashboard/getLoading",
      list: "usersAnalyticsDashboard/getAnalyticsData",
    }),

    // Normalizes whether the getter returns the raw API envelope
    // ({ body: {...} }) or just the unwrapped body. Adjust if your
    // store action always unwraps it — you can simplify to `this.list`.
    analyticsData() {
      return this.list?.body || this.list || {};
    },

    // ---- Daily Sessions Trend ----
    daily_sessions_trend() {
      const trend = this.analyticsData.daily_sessions_trend || [];

      const series = [
        {
          name: "Sessions",
          data: trend.map((item) => item.sessions),
        },
      ];

      const chartOptions = {
        chart: {
          toolbar: {
            show: false,
          },
          zoom: {
            enabled: false,
          },
        },

        stroke: {
          curve: "smooth",
          width: 3,
        },

        colors: ["#35A79D"],

        xaxis: {
          categories: trend.map((item) => item.date),
          labels: {
            formatter(val) {
              if (!val) return val;
              const d = new Date(val);
              return d.toLocaleDateString("en-US", {
                month: "short",
                day: "numeric",
              });
            },
          },
        },

        dataLabels: {
          enabled: false,
        },

        grid: {
          show: false,
          // borderColor: "#444",
        },

        fill: {
          type: "gradient",
          gradient: {
            shadeIntensity: 1,
            opacityFrom: 0.4,
            opacityTo: 0.05,
          },
        },

        tooltip: {
          theme: "dark",
          y: {
            formatter(val) {
              return `${val} sessions`;
            },
          },
        },
      };

      return { series, chartOptions };
    },

    // ---- Payment Method Distribution ----
    paymentMethodSeries() {
      return (this.analyticsData.payment_distribution || []).map(
        (item) => item.percentage
      );
    },
    paymentMethodChartOpt() {
      const dist = this.analyticsData.payment_distribution || [];
      const labels = dist.map((item) => this.capitalize(item.payment_type));

      return {
        chart: {
          type: "pie",
          background: "transparent",
          toolbar: {
            show: false,
          },
        },

        labels,

        colors: ["#35A79D", "#F5B315", "#64A9DC", "#28A745", "#5DADE2"],

        legend: {
          show: true,
          position: "top",
          horizontalAlign: "center",
          fontSize: "14px",
          labels: {
            colors: "#cfcfcf",
          },
          markers: {
            width: 14,
            height: 14,
            radius: 2,
          },
        },

        dataLabels: {
          enabled: false,
        },

        stroke: {
          show: true,
          colors: ["#ffffff"],
          width: 3,
        },

        tooltip: {
          theme: "dark",
          y: {
            formatter(val, opts) {
              const item = dist[opts?.seriesIndex];
              const count = item ? item.count : undefined;
              return count !== undefined
                ? `${val}% (${count} txns)`
                : `${val}%`;
            },
          },
        },

        title: {
          text: "Payment Method Distribution",
          align: "left",
          style: {
            fontSize: "22px",
            fontWeight: 600,
            color: "#fff",
          },
        },
      };
    },

    // ---- User Segment Distribution ----
    // Segments with 0% are filtered out so the pie doesn't render
    // empty slices (your sample data only has "Power" active).
    activeSegments() {
      return (this.analyticsData.segment_distribution || []).filter(
        (item) => item.percentage > 0
      );
    },
    userSegmentSeries() {
      return this.activeSegments.map((item) => item.percentage);
    },
    userSegmentChartOpt() {
      const segments = this.activeSegments;
      const labels = segments.map((item) => item.segment);

      return {
        chart: {
          type: "pie",
          toolbar: {
            show: false,
          },
          background: "transparent",
        },

        title: {
          text: "User Segment Distribution",
          align: "left",
          style: {
            color: "#fff",
            fontSize: "22px",
            fontWeight: 600,
          },
        },

        labels,

        colors: ["#28A745", "#F5B315", "#5DADE2", "#FF5C5C"],

        legend: {
          show: true,
          position: "top",
          horizontalAlign: "center",

          labels: {
            colors: "#cfcfcf",
          },

          markers: {
            width: 14,
            height: 14,
            radius: 2,
          },
        },

        stroke: {
          show: true,
          width: 3,
          colors: ["#fff"],
        },

        dataLabels: {
          enabled: false,
        },

        tooltip: {
          theme: "dark",
          y: {
            formatter(val, opts) {
              const item = segments[opts?.seriesIndex];
              const users = item ? item.users : undefined;
              return users !== undefined
                ? `${val}% (${users} users)`
                : `${val}%`;
            },
          },
        },
      };
    },

    // ---- Platform Distribution ----
    // Note: your API currently only returns a single "unknown" platform
    // bucket rather than an iOS/Android split. This renders whatever
    // platforms the API sends instead of assuming two fixed categories.
    platformSeries() {
      return (this.analyticsData.platform_distribution || []).map(
        (item) => item.percentage
      );
    },
    platformChartOpt() {
      const dist = this.analyticsData.platform_distribution || [];
      const labels = dist.map((item) => this.capitalize(item.platform));

      return {
        chart: {
          type: "donut",
          background: "transparent",
          toolbar: {
            show: false,
          },
        },

        title: {
          text: "Platform Distribution",
          align: "left",
          style: {
            color: "#fff",
            fontSize: "22px",
            fontWeight: 600,
          },
        },

        labels,

        colors: ["#5DADE2", "#FF5C5C", "#F5B315", "#28A745"],

        legend: {
          show: true,
          position: "top",
          horizontalAlign: "center",

          labels: {
            colors: "#cfcfcf",
          },

          markers: {
            width: 14,
            height: 14,
            radius: 2,
          },
        },

        plotOptions: {
          pie: {
            donut: {
              size: "48%",
            },
          },
        },

        stroke: {
          show: true,
          width: 3,
          colors: ["#fff"],
        },

        dataLabels: {
          enabled: false,
        },

        tooltip: {
          theme: "dark",
          y: {
            formatter(val, opts) {
              const item = dist[opts?.seriesIndex];
              const users = item ? item.users : undefined;
              return users !== undefined
                ? `${val}% (${users} users)`
                : `${val}%`;
            },
          },
        },
      };
    },
  },

  methods: {
    capitalize(str) {
      if (!str) return str;
      return str.charAt(0).toUpperCase() + str.slice(1);
    },
  },
};
</script>
<style lang="sass" scoped>

.boxes
  display: flex
  flex-wrap: wrap
  gap: 16px


.boxes .v-card
  flex: 0 0 calc(50% - 8px)
  max-width: calc(50% - 8px)
  box-sizing: border-box

.header
  display: flex
  flex-direction: column
  justify-content: space-between
  gap: 1.5rem
  .label
    font-size: 1.5rem !important
  .value
    font-size: 2rem !important
    font-weight: 600
    color : $theme-color

.content
    color: #28a745
    div:last-child
        font-size: 0.875rem

.card-color
    border-radius: 10px
    border-left: 0.5rem solid $theme-color !important




@media only screen and (max-width: 960px)
    .boxes
      gap: 12px

    .boxes .v-card
      flex: 0 0 100%
      max-width: 100%
</style>
